/**
 * TypeBadge - Badge de tipología de infraestructura
 * 
 * Muestra el tipo de infraestructura con colores Material Design.
 * Ancho fijo para alineación consistente en tablas.
 * 
 * @source P1_Normaliza_v7_fixed.html
 * @version 1.0.0
 */

import React from 'react';
import { colors, typography, borderRadius, type TypologyCode } from '@/design-system/v2';

/** Tipos de infraestructura soportados */
export type InfrastructureType = 
  | 'sanitario' 
  | 'educativo' 
  | 'policial' 
  | 'cultural' 
  | 'religioso' 
  | 'emergencias' 
  | 'deportivo' 
  | 'administrativo' 
  | 'infraestructura';

/** Abreviaturas para códigos cortos (3 letras) */
export type TypeCode = 'san' | 'edu' | 'pol' | 'cul' | 'rel' | 'eme' | 'dep' | 'adm' | 'inf';

/** Mapeo código → tipo completo */
const CODE_TO_TYPE: Record<TypeCode, InfrastructureType> = {
  san: 'sanitario',
  edu: 'educativo',
  pol: 'policial',
  cul: 'cultural',
  rel: 'religioso',
  eme: 'emergencias',
  dep: 'deportivo',
  adm: 'administrativo',
  inf: 'infraestructura',
};

/** Mapeo tipo completo → código */
const TYPE_TO_CODE: Record<InfrastructureType, TypeCode> = {
  sanitario: 'san',
  educativo: 'edu',
  policial: 'pol',
  cultural: 'cul',
  religioso: 'rel',
  emergencias: 'eme',
  deportivo: 'dep',
  administrativo: 'adm',
  infraestructura: 'inf',
};

/** Mapeo tipo → etiqueta visible (fallback si v2 no tiene name) */
const TYPE_LABELS: Record<InfrastructureType, string> = {
  sanitario: 'Sanitario',
  educativo: 'Educativo',
  policial: 'Seguridad',
  cultural: 'Cultural',
  religioso: 'Religioso',
  emergencias: 'Emergencias',
  deportivo: 'Deportivo',
  administrativo: 'Administrativo',
  infraestructura: 'Infraestructura',
};

export interface TypeBadgeProps {
  /** Tipo de infraestructura (nombre completo o código de 3 letras) */
  type: InfrastructureType | TypeCode;
  /** Ancho personalizado (default: 84px) */
  width?: number | string;
}

const STYLES = {
  badge: {
    display: 'inline-block',
    minWidth: 96,
    textAlign: 'center' as const,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    padding: '4px 8px',
    borderRadius: borderRadius.sm,
    whiteSpace: 'nowrap' as const,
    boxSizing: 'border-box' as const,
  },
};

/**
 * Normaliza el tipo: convierte nombre completo a código de 3 letras
 */
function normalizeToCode(type: InfrastructureType | TypeCode): TypeCode {
  if (type.length === 3 && type in CODE_TO_TYPE) {
    return type as TypeCode;
  }
  return TYPE_TO_CODE[type as InfrastructureType] ?? 'inf';
}

export const TypeBadge: React.FC<TypeBadgeProps> = ({ type, width }) => {
  const code = normalizeToCode(type);
  const typo = colors.typologies[code as TypologyCode];
  const fallbackColors = { color: '#71717a', bgAlpha: 'rgba(113, 113, 122, 0.15)', name: type };
  const { color, bgAlpha, name } = typo ?? fallbackColors;
  const label = name || TYPE_LABELS[CODE_TO_TYPE[code]] || type;

  const style: React.CSSProperties = {
    ...STYLES.badge,
    minWidth: width ?? STYLES.badge.minWidth,
    backgroundColor: bgAlpha,
    color,
  };

  return <span style={style}>{label}</span>;
};

export default TypeBadge;
